import { FC, useEffect, useMemo, useState } from 'react';
import { Box, Button, Container, FormControl, FormLabel, Heading, Input, Stack, Text, useToast, VStack, Badge, Divider, HStack, Icon, SimpleGrid } from '@chakra-ui/react';
import { MdCheckCircle, MdLock } from 'react-icons/md';
import { useLocation, useParams } from 'react-router-dom';
import { createLead } from './leads-service';
import { getPublicCampaignLink } from './campaign-links-service';
import { useTranslation } from 'react-i18next';
import { LoadingScreen } from '@bemap/ui';

const useQuery = () => new URLSearchParams(useLocation().search);

const CapturePage: FC = () => {
  const query = useQuery();
  const params = useParams();
  const [code, setCode] = useState('');
  const [campaignType, setCampaignType] = useState('');
  const [notFound, setNotFound] = useState(false);
  const campaignId = useMemo(() => (params as any).code ?? undefined, [params]);
  const toast = useToast();
  const { t } = useTranslation();
  const bgUrl = useMemo(() => query.get('bg') || (import.meta as any).env?.VITE_CAPTURE_BG_URL || '', [query]);
  const accent = 'blue.400';
  useEffect(() => {
    const init = async () => {
      const codeFromQuery = query.get('code');
      if (codeFromQuery) {
        setCode(codeFromQuery);
        return;
      }
      if (campaignId) {
        try {
          const res = await getPublicCampaignLink(campaignId);
          if ((res as any).statusCode && (res as any).statusCode !== 200) {
            throw new Error('Request failed');
          }
          const link = (res as any).message;
          if ((link as any).is_active && (link as any).code) {
            setCode((link as any).code);
            setCampaignType((link as any).type);
          } else {
            setCode('');
            setNotFound(true);
          }
        } catch {
          setCode('');
          setNotFound(true);
        }
      }
    };
    init();
  }, [campaignId]);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code) {
      toast({ status: 'error', title: t('lead_capture.invalid_link') || 'Invalid campaign link' });
      return;
    }
    if (!name || !email) {
      toast({ status: 'error', title: t('lead_capture.required_fields') || 'Name and email are required' });
      return;
    }
    try {
      setLoading(true);
      await createLead({ name, email, phone: phone || undefined, code });
      setSuccess(true);
    } catch (err) {
      toast({ status: 'error', title: t('lead_capture.submit_error') || 'Could not submit your info' });
    } finally {
      setLoading(false);
    }
  };

  // Loading state when resolving campaign by id
  if (campaignId && !notFound && !code) {
    return <LoadingScreen />
  }

  // Not found state
  if (notFound) {
    return (
      <Box minH="100vh" position="relative" bgImage={bgUrl ? `url(${bgUrl})` : 'https://images.pexels.com/photos/416405/pexels-photo-416405.jpeg'} bgSize="cover" bgPos="center" bgRepeat="no-repeat">
        <Box position="absolute" inset={0} bgGradient="linear(to-r, blackAlpha.800, blackAlpha.500 40%, transparent)" />
        <Container maxW="6xl" position="relative" zIndex={1} py={{ base: 12, md: 24 }}>
          <VStack align="start" spacing={4} color="white">
            <Heading textAlign="center" fontWeight="extrabold" lineHeight={1.1} fontSize={{ base: '3xl', md: '5xl' }}>{t('lead_capture.invalid_link') || 'Invalid campaign link'}</Heading>
            <Text textAlign="center" fontSize={{ base: 'md', md: 'xl' }} opacity={0.9}>{t('lead_capture.submit_error') || 'Could not submit your info'}</Text>
          </VStack>
        </Container>
      </Box>
    )
  }

  if (success) {
    return (
      <Box
        minH="100vh"
        position="relative"
        bgImage={bgUrl ? `url(${bgUrl})` : 'https://images.pexels.com/photos/416405/pexels-photo-416405.jpeg'}
        bgSize="cover"
        bgPos="center"
        bgRepeat="no-repeat"
      >
        <Box position="absolute" inset={0} bgGradient="linear(to-r, blackAlpha.800, blackAlpha.500 40%, transparent)" />
        <Container maxW="6xl" position="relative" zIndex={1} py={{ base: 8, md: 16 }}>
          <Stack direction={{ base: 'column', md: 'row' }} spacing={{ base: 6, md: 12 }} align="stretch">
            <VStack align="start" spacing={4} flex={1} color="white">
              <Badge colorScheme="whiteAlpha" bg="whiteAlpha.300" px={3} py={1} rounded="full" fontWeight="semibold">
                {t('lead_capture_a.see_demo') || 'See demo'}
              </Badge>
              <Heading fontWeight="extrabold" lineHeight={1.1} fontSize={{ base: '3xl', md: '5xl' }}>
                {t('lead_capture_a.page_title') || 'Get your free report'}
              </Heading>
              <Text fontSize={{ base: 'md', md: 'xl' }} opacity={0.95}>
                {t('lead_capture_a.subtitle') || 'Complete your details to receive a personalized report.'}
              </Text>
            </VStack>
            <Box bg="whiteAlpha.900" rounded="xl" shadow="2xl" p={{ base: 6, md: 8 }} w={{ base: '100%', md: '440px' }} alignSelf={{ base: 'stretch', md: 'center' }} borderWidth="1px" borderColor="blackAlpha.100" backdropFilter="saturate(180%) blur(8px)">
              <Heading size="md" mb={4}>{t('lead_capture_a.thanks_title') || 'Thanks!'}</Heading>
              <Text>{t('lead_capture_a.thanks_text') || 'You will receive your report by email shortly.'}</Text>
            </Box>
          </Stack>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      minH="100vh"
      position="relative"
      bgImage={bgUrl ? `url(${bgUrl})` : 'https://images.pexels.com/photos/416405/pexels-photo-416405.jpeg'}
      bgSize="cover"
      bgPos="center"
      bgRepeat="no-repeat"
    >
      <Box position="absolute" inset={0} bgGradient="linear(to-r, blackAlpha.800, blackAlpha.500 40%, transparent)" />
      <Container maxW="6xl" position="relative" zIndex={1} py={{ base: 8, md: 16 }}>
        <Stack direction={{ base: 'column', md: 'row' }} spacing={{ base: 8, md: 12 }} align="stretch">
          <VStack align="start" spacing={5} flex={1} color="white" justify={{ base: 'flex-start', md: 'center' }}>
            <Heading fontWeight="extrabold" lineHeight={1.1} fontSize={{ base: '3xl', md: '5xl' }}>
              {t(`lead_capture_${campaignType}.page_title`) || 'Get your free report'}
            </Heading>
            <Text fontSize={{ base: 'md', md: 'xl' }} opacity={0.95}>
              {t(`lead_capture_${campaignType}.subtitle`) || 'Complete your details to receive a personalized report.'}
            </Text>
            <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3} pt={2}>
              <HStack spacing={2}>
                <Icon as={MdCheckCircle} color={accent} />
                <Text>3 min</Text>
              </HStack>
              <HStack spacing={2}>
                <Icon as={MdLock} color={accent} />
                <Text>{t('lead_capture.privacy') || 'No spam'}</Text>
              </HStack>
              <HStack spacing={2} display={{ base: 'none', md: 'flex' }}>
                <Icon as={MdCheckCircle} color={accent} />
                <Text>{t('lead_capture.free') || 'Free'}</Text>
              </HStack>
            </SimpleGrid>
          </VStack>
          <Box bg="whiteAlpha.900" rounded="xl" shadow="2xl" p={{ base: 6, md: 8 }} w={{ base: '100%', md: '440px' }} alignSelf={{ base: 'stretch', md: 'center' }} borderWidth="1px" borderColor="blackAlpha.100" backdropFilter="saturate(180%) blur(8px)">
            <Heading size="md" mb={4}>{t(`lead_capture_${campaignType}.see_demo`) || 'See demo'}</Heading>
            <form onSubmit={onSubmit}>
              <Stack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>{t('lead_capture.name') || 'Name'}</FormLabel>
                  <Input size="lg" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('lead_capture.name_placeholder') || 'Your name'} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Email</FormLabel>
                  <Input size="lg" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('lead_capture.email_placeholder') || 'you@example.com'} />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('lead_capture.phone') || 'Phone'}</FormLabel>
                  <Input size="lg" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t('lead_capture.phone_placeholder') || '(555) 555-5555'} />
                </FormControl>
                <Button size="lg" w="full" type="submit" colorScheme="blue" isLoading={loading} isDisabled={!code}>{t(`lead_capture_${campaignType}.submit`) || 'Get report'}</Button>
                <Divider />
                <Text fontSize="sm" color="gray.500">{t('lead_capture.privacy_line') || "We respect your privacy. You'll receive only the requested content."}</Text>
                {!code && <Text color="red.200">{t('lead_capture.invalid_link') || 'Invalid campaign link.'}</Text>}
              </Stack>
            </form>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
export default CapturePage;


